import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";

const SectionErrorsContext = createContext(null);

export const SECTIONS = Array.from({ length: 11 }, (_, i) => `section${i + 1}`);

export function SectionErrorsProvider({ children }) {
  // Store error keys per section (e.g. section2: { dob: true, ni: true })
  const [errorsBySection, setErrorsBySection] = useState(() =>
    SECTIONS.reduce((acc, s) => {
      acc[s] = {};
      return acc;
    }, {}),
  );

  const setFieldError = useCallback(
    (sectionKey, fieldKey, hasError, message = "") => {
      setErrorsBySection((prev) => {
        const next = { ...prev };
        const sectionErrors = { ...(next[sectionKey] || {}) };

        if (hasError) sectionErrors[fieldKey] = message || true;
        else delete sectionErrors[fieldKey];

        next[sectionKey] = sectionErrors;
        return next;
      });
    },
    [],
  );

  const clearSectionErrors = useCallback((sectionKey) => {
    setErrorsBySection((prev) => ({ ...prev, [sectionKey]: {} }));
  }, []);

  const hasSectionErrors = useCallback(
    (sectionKey) => Object.keys(errorsBySection[sectionKey] || {}).length > 0,
    [errorsBySection],
  );

  const isFormValid = useMemo(() => {
    return SECTIONS.every(
      (s) => Object.keys(errorsBySection[s] || {}).length === 0,
    );
  }, [errorsBySection]);

  const value = useMemo(
    () => ({
      errorsBySection,
      setFieldError,
      clearSectionErrors,
      hasSectionErrors,
      isFormValid,
    }),
    [
      errorsBySection,
      setFieldError,
      clearSectionErrors,
      hasSectionErrors,
      isFormValid,
    ],
  );

  return (
    <SectionErrorsContext.Provider value={value}>
      {children}
    </SectionErrorsContext.Provider>
  );
}

export function useSectionErrors() {
  const ctx = useContext(SectionErrorsContext);
  if (!ctx)
    throw new Error(
      "useSectionErrors must be used inside SectionErrorsProvider",
    );
  return ctx;
}
